import { UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { FilterBar, SearchField } from "@/components/filter-bar";
import { Pager } from "@/components/pager";
import { SearchMultiSelect } from "@/components/search-multi-select";
import { TimeRangeFilter } from "@/components/time-range-filter";
import { activityActionLabel } from "@/lib/activity-actions";
import { type AdminUserSummary, useAdminActivity } from "@/lib/api";
import { useLanguage } from "@/lib/i18n/language";
import { accountName } from "@/lib/navigation";
import { useUrlTableSort } from "@/lib/table-sort";
import { sameTimeRange, useTimeRange, withTimeRangeParams } from "@/lib/time-range";

import { ActionIcon } from "./action-icon";
import { ACTIVITY_SORT_COLUMNS, ActivityTable } from "./activity-table";
import {
  activitySortOrder,
  activityTimeBounds,
  LOGBOOK_PARAMS,
  LOGBOOK_RANGE,
  LOGBOOK_RANGE_URL,
} from "./utils";

interface Props {
  users: AdminUserSummary[];
  actions: string[];
}

const PAGE_SIZE = 25;

/** How long the search waits after the last key before it asks the server. */
const SEARCH_DELAY_MS = 300;

/**
 * The activity log, browsable: a search on a line of its own, then a filter
 * row (the time range, people and actions, each a list you can type in, and
 * "Filters wissen" once one is set), the entries, and the pages. The time
 * range opens on the past month; a period chosen here is also the period of
 * the figures above. The search, the time range, the people, the actions and
 * the sort live in the URL (`?q=…&days=90&person=…&action=…&sort=…`), so a
 * reload or a shared link opens the same log. All filtering, searching,
 * sorting and paging is server-side: every change asks `useAdminActivity`
 * again, goes back to the first page, and the rows on screen dim until the
 * answer is in. A sort by action follows the words the table shows in the
 * dashboard's language, not the codes the server holds.
 */
export function ActivityExplorer({ users, actions }: Props) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [params, setParams] = useSearchParams();
  const search = params.get(LOGBOOK_PARAMS.search) ?? "";
  const [range, setRange] = useTimeRange(LOGBOOK_RANGE_URL);
  const chosenActions = params.getAll(LOGBOOK_PARAMS.action);
  const userIds = params.getAll(LOGBOOK_PARAMS.person);
  // Sorted by the server, across the pages.
  const sort = useUrlTableSort({
    columns: ACTIVITY_SORT_COLUMNS,
    initial: { key: "created_at", dir: "desc" },
  });

  // The search as the server gets it: a while after the last key, so not on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // The page belongs to one set of filters and one sort: any change starts again at the first.
  const signature = JSON.stringify([debouncedSearch, range, chosenActions, userIds, sort.sort]);
  const [paging, setPaging] = useState({ signature, page: 1 });
  const page = paging.signature === signature ? paging.page : 1;
  const setPage = (next: number) => setPaging({ signature, page: next });

  const activityQuery = useAdminActivity({
    search: debouncedSearch || undefined,
    actions: chosenActions,
    userIds,
    ...activityTimeBounds(range, new Date()),
    sortBy: sort.sort?.key,
    sortDir: sort.sort?.dir,
    // Part of the query's key, so another language asks again in the new order.
    sortOrder: activitySortOrder(sort.sort?.key, actions, language),
    page,
    pageSize: PAGE_SIZE,
  });

  const setParam = (name: string, values: readonly string[]) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(name);
        values.filter((value) => value !== "").forEach((value) => next.append(name, value));
        return next;
      },
      { replace: true },
    );

  const data = activityQuery.data ?? { items: [], total: 0, page: 1, page_size: PAGE_SIZE };
  const loading = activityQuery.isFetching;
  const filtered =
    search.trim() !== "" ||
    !sameTimeRange(range, LOGBOOK_RANGE) ||
    chosenActions.length > 0 ||
    userIds.length > 0;

  const reset = () => {
    setDebouncedSearch("");
    setParams(
      (current) => {
        const next = withTimeRangeParams(current, LOGBOOK_RANGE, LOGBOOK_RANGE_URL);
        Object.values(LOGBOOK_PARAMS).forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const from = data.total === 0 ? 0 : (data.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(data.page * PAGE_SIZE, data.total);

  return (
    <div className="space-y-4">
      {/* The search on a line of its own, so the three filters and the reset fit one line beside the settings' menu. */}
      <div className="space-y-2">
        <SearchField
          value={search}
          onChange={(value) => setParam(LOGBOOK_PARAMS.search, [value])}
          label={t("activity.search")}
        />
        <FilterBar columns={["wideRow", "medium", "medium"]} filtered={filtered} onReset={reset}>
          <TimeRangeFilter value={range} onChange={setRange} className="w-full" />
          <SearchMultiSelect
            options={users.map((user) => ({
              value: user.id,
              label: accountName(user),
              detail: user.email,
              icon: <UserRound strokeWidth={1.75} className="text-primary size-4" />,
            }))}
            value={userIds}
            onChange={(next) => setParam(LOGBOOK_PARAMS.person, next)}
            name={t("activity.people")}
            allLabel={t("activity.allPeople")}
            countLabel={(count) => t("filters.personCount", { count })}
            searchLabel={t("activity.searchPerson")}
            className="w-full"
          />
          <SearchMultiSelect
            // No second line: an action's stored code means nothing to an admin.
            options={actions.map((action) => ({
              value: action,
              label: activityActionLabel(action),
              icon: <ActionIcon action={action} className="text-primary size-4" />,
            }))}
            value={chosenActions}
            onChange={(next) => setParam(LOGBOOK_PARAMS.action, next)}
            name={t("activity.actionsFilter")}
            allLabel={t("activity.allActions")}
            countLabel={(count) => t("filters.actionCount", { count })}
            searchLabel={t("activity.searchAction")}
            className="w-full"
          />
        </FilterBar>
      </div>

      <ActivityTable
        entries={data.items}
        searchTerm={debouncedSearch}
        sort={sort}
        loading={loading}
        onReset={filtered ? reset : undefined}
      />

      <Pager
        from={from}
        to={to}
        total={data.total}
        position={t("activity.page", { page: data.page, total: totalPages })}
        previousLabel={t("activity.previous")}
        nextLabel={t("activity.next")}
        canPrevious={data.page > 1 && !loading}
        canNext={data.page < totalPages && !loading}
        onPrevious={() => setPage(Math.max(1, page - 1))}
        onNext={() => setPage(page + 1)}
      />
    </div>
  );
}
